/**
 * FormField — integriert Label + Input + Error-Message + Description in eine
 * a11y-konforme Einheit.
 *
 * Pattern:
 *   <FormField label="Email" error={errors.email?.message} hint="...">
 *     <Input {...register('email')} />
 *   </FormField>
 *
 * - Label.htmlFor verlinkt automatisch via generated ID
 * - aria-invalid wird gesetzt wenn error vorhanden ist
 * - aria-describedby zeigt auf hint oder error
 * - Error-Message ist role="alert" (Screenreader hört sie sofort)
 */
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Label } from './Input';

export interface FormFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function FormField({ label, hint, error, required, className, children }: FormFieldProps) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  // Wenn das Kind ein einzelnes React-Element ist, hängen wir id + aria-Props automatisch dran.
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? inputId,
        'aria-invalid': error ? 'true' : undefined,
        'aria-describedby': error ? errorId : hint ? hintId : undefined,
      })
    : children;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <Label htmlFor={inputId}>
          {label}
          {required && <span className="text-sealwax ml-1">*</span>}
        </Label>
      )}
      {child}
      {error && (
        <p id={errorId} role="alert" className="text-sealwax text-xs">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-ink-faint text-xs">
          {hint}
        </p>
      )}
    </div>
  );
}
